/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\TypeForme.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

/**
SELLERIE MARINE - COMPOSANT TYPE ET FORME

📋 DESCRIPTION:
Composant React pour la sélection du type de produit et de sa forme dans le configurateur d'articles.
Interface spécialisée pour choix coussin/bâche avec formes adaptatives.

🎯 RESPONSABILITÉS:
- Sélection du type de produit (coussin ou bâche)
- Adaptation des options de forme selon le type sélectionné
- Gestion du talon (coussins) ou ourlet (bâches) avec validation
- Interface adaptative avec descriptions contextuelles
- Notification des modifications au composant parent
- Validation des valeurs selon le type de produit

🔗 DÉPENDANCES:
- types.ts: Interface Article pour typage TypeScript
- React: Hooks useEffect pour logs et surveillance
- CSS: Classes utilitaires pour interface utilisateur

📡 INTERACTIONS:
- Parent: Réception données article et fonction handleChange
- Parent: Notification modifications via callback onDataChange
- Interface: Validation en temps réel des saisies
- Logs: Surveillance des changements pour débogage

📊 STATUT: ACTIF (Production)
📅 Dernière mise à jour: Juin 2025
👤 Responsable: Équipe développement

💡 NOTES:
- Adaptation automatique des formes selon type sélectionné
- Validation différenciée talon (0.1-10cm) vs ourlet (0.1-50cm)
- Interface contextuelle avec descriptions d'aide
- Logs de débogage pour surveillance des changements
- Wrapper de notification pour synchronisation parent
*/
import React, { useEffect, useState } from 'react';
import { Article } from './types';
import ModalSelectionForme from './components/ModalSelectionForme';
import ModalPhotogrammetrie from './components/ModalPhotogrammetrie';

// ✅ ÉTAPE 1 : Interface Props modifiée
interface TypeFormeProps {
  article: Article;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, section?: string, subsection?: string) => void;
  onDataChange: () => void; // ✅ NOUVELLE PROP AJOUTÉE
}

// ✅ ÉTAPE 2 : Composant modifié avec onDataChange
const TypeForme: React.FC<TypeFormeProps> = ({ 
  article, 
  handleChange,
  onDataChange // ✅ NOUVELLE PROP AJOUTÉE
}) => {
  // État pour les modales
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPhotogrammetrieModalOpen, setIsPhotogrammetrieModalOpen] = useState(false);

  // ✅ ÉTAPE 3 : Fonction wrapper qui notifie les changements
  const handleChangeWithNotification = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, section?: string, subsection?: string) => {
    handleChange(e, section, subsection);
    onDataChange(); // Notifier le parent que des données ont été modifiées
  };

  // Fonction pour gérer la sélection de forme depuis la modale
  const handleFormeSelected = (forme: string) => {
    const event = {
      target: { name: 'forme', value: forme }
    } as React.ChangeEvent<HTMLSelectElement>;
    
    handleChangeWithNotification(event);
    
    // Si on sélectionne "Rectangle Boîte" et que la hauteur n'est pas définie, mettre 10 par défaut
    if (forme === 'rectangle_boite' && !article.dimensions.hauteur) {
      const hauteurEvent = {
        target: { name: 'hauteur', value: '10' }
      } as React.ChangeEvent<HTMLInputElement>;
      handleChangeWithNotification(hauteurEvent, 'dimensions');
    }
    
    // Valeurs par défaut pour les formes biseau
    if (forme === 'rectangle_joues_biseau' || forme === 'rectangle_demi_bandeau_biseau') {
      // Longueur L (déjà ok avec valeur existante ou défaut)
      if (!article.dimensions.longueur) {
        const longueurEvent = {
          target: { name: 'longueur', value: '60' }
        } as React.ChangeEvent<HTMLInputElement>;
        handleChangeWithNotification(longueurEvent, 'dimensions');
      }
      
      // Largeur dessus l1 = 30
      const largeurDessusEvent = {
        target: { name: 'largeur_dessus', value: '30' }
      } as React.ChangeEvent<HTMLInputElement>;
      handleChangeWithNotification(largeurDessusEvent, 'dimensions');
      
      // Largeur dessous l2 = 25
      const largeurDessousEvent = {
        target: { name: 'largeur_dessous', value: '25' }
      } as React.ChangeEvent<HTMLInputElement>;
      handleChangeWithNotification(largeurDessousEvent, 'dimensions');
      
      // Hauteur H = 10
      const hauteurEvent = {
        target: { name: 'hauteur', value: '10' }
      } as React.ChangeEvent<HTMLInputElement>;
      handleChangeWithNotification(hauteurEvent, 'dimensions');
    }
  };

  // Fonction pour gérer les résultats de la photogrammétrie
  const handlePhotogrammetrieResult = (result: any) => {
    // Utiliser la forme détectée automatiquement ou fallback
    let formeDetectee = 'rectangulaire_joues'; // Défaut
    
    if (result.forme_detectee && result.forme_detectee.forme) {
      formeDetectee = result.forme_detectee.forme;
      console.log(`Forme détectée automatiquement: ${formeDetectee} (confiance: ${result.forme_detectee.confidence})`);
    }
    
    const formeEvent = {
      target: { name: 'forme', value: formeDetectee }
    } as React.ChangeEvent<HTMLSelectElement>;
    handleChangeWithNotification(formeEvent);

    // Appliquer les dimensions détectées
    const longueurEvent = {
      target: { name: 'longueur', value: result.dimensions.longueur.toString() }
    } as React.ChangeEvent<HTMLInputElement>;
    handleChangeWithNotification(longueurEvent, 'dimensions');

    const largeurEvent = {
      target: { name: 'largeur', value: result.dimensions.largeur.toString() }
    } as React.ChangeEvent<HTMLInputElement>;
    handleChangeWithNotification(largeurEvent, 'dimensions');

    const hauteurEvent = {
      target: { name: 'hauteur', value: result.dimensions.hauteur.toString() }
    } as React.ChangeEvent<HTMLInputElement>;
    handleChangeWithNotification(hauteurEvent, 'dimensions');

    // Marquer l'article comme créé par photogrammétrie
    const photogrammetrieEvent = {
      target: { name: 'photogrammetrie_session', value: result.session_id }
    } as React.ChangeEvent<HTMLInputElement>;
    handleChangeWithNotification(photogrammetrieEvent);
  };

  // Fonction pour obtenir le nom d'affichage de la forme
  const getFormeDisplayName = (forme: string) => {
    const formeNames: { [key: string]: string } = {
      'rectangulaire_bandeau': 'Rectangulaire avec bandeau',
      'rectangulaire_demi_bandeau': 'Rectangle demi-bandeau',
      'rectangulaire_joues': 'Rectangulaire avec joues',
      'rectangle_joues_biseau': 'Rectangle joues biseau',
      'rectangle_demi_bandeau_biseau': 'Rectangle demi-bandeau biseau',
      'rectangle_boite': 'Rectangle Boîte',
      'rond_avec_bandeau': 'Rond avec bandeau',
      'cylindre_joues': 'Cylindre avec joues',
      'rectangle_simple': 'Rectangle simple'
    };
    return formeNames[forme] || forme;
  };


  return (
    <>
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-6">Type et forme</h2>
        
        {/* Layout en grille pour une meilleure organisation */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Type de produit avec icônes - ANCIEN SYSTÈME CONSERVÉ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Type de produit
            </label>
            <div className="grid grid-cols-2 gap-3">
              {/* Bouton Coussin avec icône */}
              <button
                type="button"
                onClick={() => {
                  const event = {
                    target: { name: 'type', value: 'coussin' }
                  } as React.ChangeEvent<HTMLSelectElement>;
                  handleChangeWithNotification(event);
                  // Ouvrir automatiquement la modale si on change de type
                  setIsModalOpen(true);
                }}
                className={`flex flex-col items-center p-3 border rounded-lg transition-colors ${
                  article.type === 'coussin' ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white hover:bg-gray-50'
                }`}
              >
                <svg
                  viewBox="0 0 64 64"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="w-10 h-10 mb-2"
                >
                  <path d="M12 20 c0 -8 6 -14 14 -14 h12 c8 0 14 6 14 14 v24 c0 8 -6 14 -14 14 H26 c-8 0 -14 -6 -14 -14 Z" />
                  <path d="M20 32 h24" strokeWidth="4" />
                </svg>
                <span className="text-sm font-medium">Coussin</span>
              </button>

              {/* Bouton Bâche avec icône */}
              <button
                type="button"
                onClick={() => {
                  const event = {
                    target: { name: 'type', value: 'bache' }
                  } as React.ChangeEvent<HTMLSelectElement>;
                  handleChangeWithNotification(event);
                  // Ouvrir automatiquement la modale si on change de type
                  setIsModalOpen(true);
                }}
                className={`flex flex-col items-center p-3 border rounded-lg transition-colors ${
                  article.type === 'bache' ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-white hover:bg-gray-50'
                }`}
              >
                <svg
                  viewBox="0 0 64 64"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="w-10 h-10 mb-2"
                >
                  <rect x="8" y="16" width="48" height="32" rx="2" />
                  <circle cx="16" cy="24" r="2" fill="currentColor" />
                  <circle cx="48" cy="24" r="2" fill="currentColor" />
                  <circle cx="16" cy="40" r="2" fill="currentColor" />
                  <circle cx="48" cy="40" r="2" fill="currentColor" />
                </svg>
                <span className="text-sm font-medium">Bâche</span>
              </button>
            </div>
          </div>
          
          {/* NOUVELLE INTERFACE FORME */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Forme
            </label>
            <div className="space-y-3">
              {/* Affichage de la forme sélectionnée avec icône */}
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                <div className="flex items-center space-x-3 mb-2">
                  {/* Icône de la forme - SEULEMENT SI UNE FORME EST SÉLECTIONNÉE */}
                  {article.forme ? (
                    <img
                      src={`/images/formes/${article.type === 'coussin' ? 'coussins' : 'baches'}/${
                        article.type === 'coussin'
                          ? (() => {
                              switch(article.forme) {
                                case 'cylindre_joues': return 'cylindre_avec_joues.ico';
                case 'rectangulaire_bandeau': return 'rectanglulaire_bandeau.ico';
                case 'rectangulaire_demi_bandeau': return 'rectanglulaire_demi_bandeau.ico';
                case 'rectangulaire_joues': return 'rectangle_avec_joues.ico';
                case 'rectangle_boite': return 'rectangle_boite.ico';
                case 'rond_avec_bandeau': return 'rond_avec_bandeau.ico';
                                default: return 'cylindre-joues.ico';
                              }
                            })()
                          : 'Bache.ico'
                      }`}
                      alt={getFormeDisplayName(article.forme)}
                      className="w-8 h-8 object-contain flex-shrink-0"
                      onError={(e) => {
                        // Fallback vers une icône générique si l'image ne charge pas
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iNCIgeT0iNCIgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiByeD0iNCIgc3Ryb2tlPSIjOTk5IiBzdHJva2Utd2lkdGg9IjIiLz4KPHN2Zz4K';
                      }}
                    />
                  ) : (
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-200 rounded border-2 border-dashed border-gray-400">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {article.forme ? getFormeDisplayName(article.forme) : 'Sélectionnez une forme'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {article.type === 'coussin' ? 'Coussin' : 'Bâche'}
                    </div>
                  </div>
                </div>
                
                {/* Boutons d'action */}
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="w-full px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                  >
                    {article.forme ? 'Changer' : 'Sélectionner'}
                  </button>
                  
                  {/* Bouton photogrammétrie pour coussins uniquement */}
                  {article.type === 'coussin' && (
                    <button
                      type="button"
                      onClick={() => setIsPhotogrammetrieModalOpen(true)}
                      className="w-full px-3 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                    >
                      <span>📸</span>
                      <span>D'après photos</span>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Message d'aide */}
              <div className="text-xs text-gray-500">
                {article.type === 'coussin' 
                  ? (article.forme 
                      ? 'Cliquez sur "Changer" pour modifier la forme sélectionnée'
                      : 'Cliquez sur "Sélectionner" pour choisir parmi 5 formes de coussins'
                    )
                  : 'Forme automatique pour les bâches rectangulaires'
                }
              </div>
            </div>
          </div>
          
          {/* Section Talon/Ourlet */}
          <div>
            {article.type === 'bache' ? (
              <>
                <label htmlFor="talon" className="block text-sm font-medium text-gray-700 mb-1">
                  Ourlet (cm)
                </label>
                <div className="text-xs text-gray-500 mb-2">
                  Marge supplémentaire pour l'ourlet des bâches (généralement entre 2 et 5 cm)
                </div>
              </>
            ) : (
              <>
                <label htmlFor="talon" className="block text-sm font-medium text-gray-700 mb-1">
                  Talon (cm)
                </label>
                <div className="text-xs text-gray-500 mb-2">
                  Marge supplémentaire pour les coutures des coussins (généralement entre 1 et 2 cm)
                </div>
              </>
            )}
            <input
              type="number"
              id="talon"
              name="talon"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={article.dimensions.talon || ''}
              onChange={(e) => handleChangeWithNotification(e, 'dimensions')}
              min="0.1"
              max={article.type === 'bache' ? 50 : 10}
              step="0.1"
              required
              placeholder={article.type === 'bache' ? "Valeur de l'ourlet en cm" : "Valeur du talon en cm"}
            />
          </div>
        </div>
      </div>

      {/* MODALE DE SÉLECTION DE FORME */}
      <ModalSelectionForme
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onFormeSelected={handleFormeSelected}
        typeArticle={article.type as 'coussin' | 'bache'}
        formeActuelle={article.forme}
      />

      {/* MODALE PHOTOGRAMMÉTRIE */}
      <ModalPhotogrammetrie
        isOpen={isPhotogrammetrieModalOpen}
        onClose={() => setIsPhotogrammetrieModalOpen(false)}
        onResult={handlePhotogrammetrieResult}
      />
    </>
  );
};

export default TypeForme;
